// Data layer: manifest + lazy chunk/full loaders with a small in-memory cache.
// Backed by data_v2/ produced by scripts/build_data.py.
import { setImageDims } from './markdown.js';
import { compLabel } from './i18n.js';

const DATA_ROOT = 'data_v2';

const cache = {
    manifest: null,        // { rows, schema, domains }
    manifestP: null,       // in-flight manifest promise (dedup concurrent callers)
    sources: null,         // array of source meta
    competitions: null,    // array of competition meta
    topicGraph: null,      // derived гурток taxonomy DAG view (see loadTopicGraph)
    topicGraphP: null,     // in-flight topic-graph promise (dedup)
    stats: null,
    version: null,
    imageDims: null,       // { "<filename>": [w,h] } figure-dimensions sidecar
    imageDimsP: null,      // in-flight image-dims promise (dedup)
    chunks: new Map(),     // `${src}/${year}` -> rows
    full: new Map(),       // slug -> full record
};

// version.json is fetched without a version qualifier (network-first below) so
// a data-layout rebuild is noticed. Every other data_v2/* URL gets `?v=<token>`
// appended — that both namespaces the cache per build and guarantees the
// CDN/disk cache-first path serves the freshly built artifacts.
// The token is per file when version.json lists one (`files`, written by
// build_data.stamp_versions: it changes only when that file's bytes do), so a
// publish that leaves the manifest or search.json as they were costs a
// returning visitor nothing. A file it does not list (chunks/, full/, ids/), or
// an older version.json without `files`, falls back to the global `version`.
let versionTag = null;
let versionFiles = {};
let versionP = null;
async function getVersionTag() {
    if (versionTag) return versionTag;
    if (versionP) return versionP;
    versionP = (async () => {
        try {
            // Bust any cache on version.json itself — it's the anchor.
            const res = await fetch(`${DATA_ROOT}/version.json`, { cache: 'no-store' });
            if (res.ok) {
                const j = await res.json();
                cache.version = j;
                versionFiles = filesOf(j);
                versionTag = j.version || '';
                return versionTag;
            }
        } catch (_) { /* fall through */ }
        versionTag = '';
        return versionTag;
    })();
    return versionP;
}

// version.json's per-file tokens, {} when it has none (an older exporter).
export function filesOf(j) {
    return (j && j.files && typeof j.files === 'object') ? j.files : {};
}

// The ?v= token for one data file (`manifest.json`, `full/<slug>.json`): its
// own when version.json lists it, else the build's `version`. Shared with
// js/search/worker.js so the worker asks for the very URLs the page did.
export function versionFor(files, version, path) {
    const own = files && files[path];
    return (typeof own === 'string' && own) ? own : (version || '');
}

// DecompressionStream is supported everywhere except very old browsers and
// JSDOM; we feature-detect once and cache the answer.
const SUPPORTS_GZIP_STREAM = typeof DecompressionStream !== 'undefined';

async function fetchJson(path) {
    const version = await getVersionTag();   // first: it fills versionFiles
    const v = versionFor(versionFiles, version, path);
    const qs = v ? `?v=${encodeURIComponent(v)}` : '';

    // Try the pre-compressed sibling first. build_data.py emits every
    // data_v2/*.json alongside a .json.gz (~5-6x smaller on the hot files).
    // The server doesn't set Content-Encoding, so we fetch the .gz as opaque
    // bytes and decompress client-side.
    if (SUPPORTS_GZIP_STREAM) {
        try {
            const res = await fetch(`${DATA_ROOT}/${path}.gz${qs}`);
            if (res.ok) {
                const ds = new DecompressionStream('gzip');
                const stream = res.body.pipeThrough(ds);
                const text = await new Response(stream).text();
                return JSON.parse(text);
            }
            // Any non-ok status (404 when the .gz isn't deployed, 5xx, etc.)
            // falls through to the plain-JSON path below so the site keeps
            // working even on a partial deploy.
        } catch (_) {
            // Network / decompression error — fall through.
        }
    }
    const res = await fetch(`${DATA_ROOT}/${path}${qs}`);
    if (!res.ok) throw new Error(`${path}: ${res.status}`);
    return res.json();
}

// Manifest rows ([g,s,y,c,…] arrays) → the row objects every view filters.
// Pure, so the site proxy (tools/gcp/site-proxy/ssr.js) builds the very same
// rows to server-render /search.
export function manifestRows(m) {
    // schema: [g=slug, s=source_slug, y=year, c=comp_code, f=flag_bitfield,
    // n=problem_number, k/kx=lowest/highest grade the problem was written for
    // (0 = unknown), l=level («Рівень змагання»), a=answer format («Формат
    // відповіді»), t=topic_ids, r=difficulty rung 1–20 (0 = never scored),
    // i=public ID («VUOM-23-24-017»)]. `e`=stage and `p`=competition_type were
    // retired 2026-09-30 (tools/facets/PLAN_level_format.md).
    //
    // EVERY column is schema-addressed (indexOf), none positional. The
    // retired `d`=domain column used to sit between `c` and `f`, so a
    // hard-coded index here silently read flags-as-domain / pnum-as-flags
    // against either manifest shape — and data_v2 and this file deploy
    // separately, so both shapes are live at once during a rollout.
    // A column the manifest predates resolves to -1 and falls back below.
    const col = {};
    for (const k of ['g', 's', 'y', 'c', 'f', 'n', 'k', 'kx', 'l', 'a', 't', 'r', 'i']) {
        col[k] = (m.schema || []).indexOf(k);
    }
    const at = (r, k, dflt) => (col[k] >= 0 ? r[col[k]] : dflt);
    return (m.rows || []).map(r => {
        // `t` is the one LIST column: a problem carries several гурток tags,
        // each an int into m.topics (a slug array). Kept as raw ids here;
        // annotateTopics below turns them into the slug Set filters test.
        const flags = at(r, 'f', 0) || 0;
        return {
            slug: at(r, 'g', null),
            source: at(r, 's', null),
            year: at(r, 'y', 0) || null,
            comp: at(r, 'c', null),
            hasImages: !!(flags & 1),
            hasSolution: !!(flags & 2),
            pnum: at(r, 'n', 0),
            // «Клас» matches every grade in [grade, gradeMax]; a pre-`kx`
            // manifest carries one grade, so the range collapses to it.
            grade: at(r, 'k', 0) || 0,
            gradeMax: at(r, 'kx', 0) || at(r, 'k', 0) || 0,
            level: at(r, 'l', null) || null,
            answer: at(r, 'a', null) || null,
            topicIds: at(r, 't', null) || [],
            // 0 = never scored, which is NOT "easy": no rung filter matches it.
            difficulty: at(r, 'r', 0) || 0,
            // The public ID; null on a pre-`i` manifest, where links fall
            // back to the slug.
            pid: at(r, 'i', '') || null,
        };
    });
}

export async function loadManifest() {
    if (cache.manifest) return cache.manifest;
    if (cache.manifestP) return cache.manifestP;
    cache.manifestP = (async () => {
        const m = await fetchJson('manifest.json');
        m.rows = manifestRows(m);
        // The tier cutoffs travel with the data ([[lo, hi, key], …], see
        // build_data.DIFFICULTY_TIERS) so this file never hardcodes a copy of
        // the rubric. Empty on an unscored corpus or a pre-`r` manifest, which
        // is what hides the «Складність» facet.
        m.difficultyTiers = Array.isArray(m.difficulty_tiers) ? m.difficulty_tiers : [];
        cache.manifest = m;
        return m;
    })().catch(err => {
        // Clear the in-flight promise so a transient failure is retryable. Without
        // this the rejected promise is handed to every later caller for the life of
        // the page, and js/main.js prefetches the manifest at boot — so one flaky
        // fetch permanently kills /search (loadTopicGraph does the same reset).
        cache.manifestP = null;
        throw err;
    });
    return cache.manifestP;
}


// ── Topic taxonomy (гурток) ────────────────────────────────────────────────
// data_v2/topic_graph.json is the taxonomy as a DAG: [{slug, name, kind, years,
// parents:[slug]}]. It is NOT a tree — 20 of ~250 nodes have two parents, so a
// node is reachable by several paths and renders under each of its parents.
//
// Returns a derived view, not the raw array:
//   bySlug      slug -> node
//   childrenOf  slug -> [node] in taxonomy order
//   roots       [node] with no parents (the 4 «Предмет» subjects)
//   ancestorsOf(slug) -> Set of slug + every ancestor slug (memoized)
//   pathsOf(slug)     -> [[name, …]] every root→node name chain (memoized)
//
// Any failure degrades to an EMPTY graph rather than
// throwing: on a partial deploy the topic facet simply doesn't render, and the
// rest of the page keeps working. The empty result is cached too (no refetch).
export function deriveTopicGraph(nodes) {
    const bySlug = new Map();
    for (const n of (Array.isArray(nodes) ? nodes : [])) {
        if (n && n.slug) bySlug.set(n.slug, n);
    }
    const childrenOf = new Map();
    const roots = [];
    for (const n of bySlug.values()) {
        const parents = (n.parents || []).filter(p => bySlug.has(p));
        if (!parents.length) { roots.push(n); continue; }
        for (const p of parents) {
            if (!childrenOf.has(p)) childrenOf.set(p, []);
            childrenOf.get(p).push(n);
        }
    }
    const ancCache = new Map();
    // Iterative walk with an explicit visited set: cheap, and immune to a cycle
    // slipping past the exporter's guard (a cycle would otherwise hang the page).
    function ancestorsOf(slug) {
        let hit = ancCache.get(slug);
        if (hit) return hit;
        const out = new Set();
        const stack = [slug];
        while (stack.length) {
            const cur = stack.pop();
            if (out.has(cur)) continue;
            out.add(cur);
            const node = bySlug.get(cur);
            if (node) for (const p of (node.parents || [])) {
                if (bySlug.has(p)) stack.push(p);
            }
        }
        ancCache.set(slug, out);
        return out;
    }
    // Every root→node chain of NAMES, for the detail-page chip tooltip. A
    // multi-parent node yields one chain per parent.
    //
    // `seen` only exists to stop a cycle from recursing forever; the taxonomy is
    // acyclic (import_topics.py rejects cycles, build_from_directus.py raises on
    // them), so it normally prunes nothing. Because a pruned result would be
    // incomplete, only the top-level call (`seen` empty) is cached — interior
    // recomputation is negligible at depth 3 with a fan-in of at most 2.
    const pathCache = new Map();
    function pathsOf(slug, seen = null) {
        if (!seen) {
            const hit = pathCache.get(slug);
            if (hit) return hit;
        }
        const node = bySlug.get(slug);
        if (!node) return [];
        const parents = (node.parents || []).filter(p => bySlug.has(p) && !(seen && seen.has(p)));
        let out;
        if (!parents.length) out = [[node.name || slug]];
        else {
            out = [];
            const next = new Set(seen || []); next.add(slug);
            for (const p of parents) {
                for (const chain of pathsOf(p, next)) out.push([...chain, node.name || slug]);
            }
        }
        if (!seen) pathCache.set(slug, out);
        return out;
    }
    return { bySlug, childrenOf, roots, ancestorsOf, pathsOf, size: bySlug.size };
}

export async function loadTopicGraph() {
    if (cache.topicGraph) return cache.topicGraph;
    if (cache.topicGraphP) return cache.topicGraphP;
    cache.topicGraphP = (async () => {
        let nodes = [];
        try {
            const j = await fetchJson('topic_graph.json');
            nodes = (j && j.topics) || [];
        } catch (_) {
            nodes = [];   // not deployed / network / parse error — degrade to empty.
        }
        cache.topicGraph = deriveTopicGraph(nodes);
        cache.topicGraphP = null;
        return cache.topicGraph;
    })();
    return cache.topicGraphP;
}

// ── Difficulty tiers ───────────────────────────────────────────────────────
// The four RUBRIC20 bands as {lo, hi, key}, from the loaded manifest. Sync, so
// renderers can call it freely; [] until the manifest has loaded (main.js
// prefetches it at boot) or when nothing is scored.
export function difficultyTiers() {
    const t = cache.manifest && cache.manifest.difficultyTiers;
    return (t || []).map(([lo, hi, key]) => ({ lo, hi, key }));
}

// The tier a rung falls in, or null (unscored, or tiers not loaded).
export function tierOf(rung) {
    if (!rung) return null;
    return difficultyTiers().find(t => rung >= t.lo && rung <= t.hi) || null;
}

// The graph if it has already loaded, else null (never fetches): lets the
// problem page paint without waiting on it.
export function topicGraphIfLoaded() {
    return cache.topicGraph || null;
}

// Synchronous name lookup for callers that can't await (router.js's tab title).
// Falls back to the slug when the graph hasn't loaded yet — never throws.
export function topicNameSync(slug) {
    const g = cache.topicGraph;
    const n = g && g.bySlug.get(slug);
    return (n && n.name) || slug || '';
}

// Annotate manifest rows in place with `topicScope`: a Set holding each tagged
// topic's slug AND all of its ancestors. Rolling the ancestors in here is what
// makes both jobs downstream trivial — checking a «Предмет»/«Розділ» matches
// every problem below it (filters.js), and a parent's facet count being the
// rolled-up descendant total (optionsFor). Kept separate from loadManifest() so
// the landing never pays for the graph fetch.
//
// This allocates a Set per tagged row, and computeRows
// calls it on every render (so, every keystroke). Both inputs — the rows and the
// dictionary — are stable for the life of the manifest, so the work is done once
// per row array and later calls short-circuit.
const annotatedRows = new WeakSet();
export async function annotateTopics(rows) {
    if (annotatedRows.has(rows)) return rows;
    const [m, graph] = await Promise.all([loadManifest(), loadTopicGraph()]);
    scopeTopics(rows, m.topics || [], graph);
    annotatedRows.add(rows);
    return rows;
}

// annotateTopics' work, synchronous and pure (the site proxy runs it too):
// `dict` is manifest.topics, `graph` a deriveTopicGraph view.
export function scopeTopics(rows, dict, graph) {
    for (const r of rows) {
        const ids = r.topicIds || [];
        if (!ids.length) { r.topicSlugs = []; r.topicScope = null; continue; }
        const slugs = [];
        const scope = new Set();
        for (const id of ids) {
            const slug = dict[id];
            if (!slug) continue;
            slugs.push(slug);
            for (const a of graph.ancestorsOf(slug)) scope.add(a);
        }
        r.topicSlugs = slugs;
        r.topicScope = scope.size ? scope : null;
    }
    return rows;
}

export async function loadSources() {
    if (cache.sources) return cache.sources;
    cache.sources = await fetchJson('sources.json');
    return cache.sources;
}

// Figure dimensions sidecar ({ "<filename>": [w,h] }, ~111 KB gz). Fetched lazily
// — only image-bearing views await it — then pushed into markdown.js so figures
// render with width/height and don't shift layout as they lazy-load. A missing
// sidecar (partial deploy) degrades silently to dimensionless figures.
export async function loadImageDims() {
    if (cache.imageDims) return cache.imageDims;
    if (cache.imageDimsP) return cache.imageDimsP;
    cache.imageDimsP = (async () => {
        let map = {};
        try { map = await fetchJson('image-dims.json') || {}; } catch (_) { map = {}; }
        cache.imageDims = map;
        setImageDims(map);
        return map;
    })();
    return cache.imageDimsP;
}

// Synchronous competition name for callers that can't await (router.js's tab
// title): the catalog's Ukrainian name once competitions.json has loaded, the
// same name the /search pill shows; before that, i18n's label or the code.
export function compNameSync(code) {
    const c = (cache.competitions || []).find(x => x.code === code);
    return (c && c.name) || compLabel(code) || code || '';
}

export async function loadCompetitions() {
    if (cache.competitions) return cache.competitions;
    cache.competitions = await fetchJson('competitions.json');
    return cache.competitions;
}

export async function loadStats() {
    if (cache.stats) return cache.stats;
    cache.stats = await fetchJson('stats.json');
    return cache.stats;
}

export async function loadChunk(sourceSlug, year) {
    const key = `${sourceSlug}/${year}`;
    if (cache.chunks.has(key)) return cache.chunks.get(key);
    const rows = await fetchJson(`chunks/${sourceSlug}/${year}.json`);
    cache.chunks.set(key, rows);
    return rows;
}

// Cached slug→record Map per chunk key. Building this costs ~0 but was
// previously redone on every page render (25 items × N pages).
const chunkByIdCache = new Map();
function chunkByIdMap(key, chunkRows) {
    let m = chunkByIdCache.get(key);
    if (!m) {
        m = new Map(chunkRows.map(c => [c.slug, c]));
        chunkByIdCache.set(key, m);
    }
    return m;
}

export async function loadFull(slug) {
    if (cache.full.has(slug)) return cache.full.get(slug);
    const rec = await fetchJson(`full/${slug}.json`);
    cache.full.set(slug, rec);
    return rec;
}

// ── Public problem IDs («VUOM-23-24-017», «IMO-2023-003», tools/ids/README.md) ──
// The reader-facing identifier, and what /problem/<ID> URLs carry. The slug
// stays the internal key (full/<slug>.json), so an ID is resolved to a slug
// first: through the manifest when it is already in memory, otherwise through
// data_v2/ids/<PREFIX>.json — one small file per competition prefix, named by
// the ID's own first segment — so a cold shared link never waits for the
// whole manifest. The middle part is a season (`23-24`, the Ukrainian
// school-year competitions) or a calendar year (`2023`, everything else).
// Mirrors tools/ids/public_id.py::ID_RE.
export const PUBLIC_ID_RE = /^([A-Z][A-Z0-9]{1,5})-(\d{4}|\d{2}-\d{2})-(\d{3,4})$/;

// A typed or linked ID in canonical form, or null if it isn't one. Readers
// type «vuom-23-24-17»; the stored form is upper case with the number padded
// to 3 digits (VUOM-23-24-017).
export function normalizePublicId(s) {
    const m = /^([A-Z][A-Z0-9]{1,5}-(?:\d{4}|\d{2}-\d{2}))-(\d{1,4})$/.exec(String(s || '').trim().toUpperCase());
    const n = m ? parseInt(m[2], 10) : 0;
    if (!n) return null;
    const pid = `${m[1]}-${String(n).padStart(3, '0')}`;
    return PUBLIC_ID_RE.test(pid) ? pid : null;
}

// The season of a competition whose ID carries one (VUOM-23-24-017), both
// years in full with an en dash — «2023–2024», the way the olympiad writes a
// school year (user, 2026-09-28) — else null.
export function seasonLabel(rec) {
    const season = /^[A-Z0-9]+-\d{2}-\d{2}-/.test((rec && rec.public_id) || '')
        && /^(\d{4})-(\d{4})$/.exec((rec && rec.academic_year) || '');
    return season ? `${season[1]}–${season[2]}` : null;
}

// How a full record names its time: its season, else its year; '' if neither.
export function periodLabel(rec) {
    return seasonLabel(rec) || String((rec && rec.year) || '');
}

const idIndex = new Map();   // prefix -> Promise<{ id: slug }>
let pidRows = null;          // id -> manifest row, built once the manifest is in
function pidMap() {
    if (!pidRows && cache.manifest) pidRows = new Map(cache.manifest.rows.filter(r => r.pid).map(r => [r.pid, r]));
    return pidRows;
}

// ID -> slug, or null when no published problem carries it.
export async function slugForPublicId(id) {
    const pid = normalizePublicId(id);
    if (!pid) return null;
    const hit = pidMap() && pidMap().get(pid);
    if (hit) return hit.slug;
    const prefix = pid.split('-')[0];
    let p = idIndex.get(prefix);
    if (!p) {
        // A missing file (an unknown prefix) is a miss, not an error; drop the
        // entry so a transient failure is retried on the next lookup.
        p = fetchJson(`ids/${prefix}.json`).catch(() => { idIndex.delete(prefix); return {}; });
        idIndex.set(prefix, p);
    }
    const index = await p;
    return index[pid] || null;
}

// The whole ID file of one prefix, for the 404 page's proof: it has to tell an
// unknown prefix (no file: «missing») from a failed request («error»), which
// slugForPublicId folds together into a miss.
export async function publicIdIndex(prefix) {
    try {
        return { status: 'ok', index: await fetchJson(`ids/${prefix}.json`) };
    } catch (e) {
        return { status: /: 404$/.test(String(e && e.message)) ? 'missing' : 'error', index: {} };
    }
}

// slug -> ID when the manifest is already loaded (null otherwise), so a link
// followed from a result card shows its canonical /problem/<ID> URL at once.
export function publicIdOfSlugSync(slug) {
    const r = slugMap ? slugMap.get(slug) : (cache.manifest && cache.manifest.rows.find(x => x.slug === slug));
    return (r && r.pid) || null;
}

// Every manifest row whose ID is `prefix` or lies under it, for the search
// box: «VUOM-23-24» (a season), «IMO-2023» (a year), or a whole ID. [] until
// the manifest is loaded.
export function rowsByPublicIdPrefix(prefix) {
    const m = pidMap();
    if (!m || !prefix) return [];
    const exact = normalizePublicId(prefix);
    if (exact) return m.has(exact) ? [m.get(exact)] : [];
    const out = [];
    for (const [pid, r] of m) if (pid.startsWith(prefix + '-')) out.push(r);
    return out;
}

// Build a Map: slug -> manifest row (for O(1) slug lookup).
let slugMap = null;
export async function getSlugMap() {
    if (slugMap) return slugMap;
    const m = await loadManifest();
    slugMap = new Map(m.rows.map(r => [r.slug, r]));
    return slugMap;
}

// Problems without a year are written to chunks/<src>/0.json by build_data.py
// (NULL years are keyed as 0). Normalize null/undefined → 0 here so the
// client hits the correct chunk file.
function chunkYear(y) { return (y == null || y === 0) ? 0 : y; }

// Helpers for list rendering: fetch preview/title for a batch of rows by
// loading their chunks once. Chunks are shared across many rows.
export async function hydrateCards(rows) {
    const need = new Map();   // key -> rows[]
    for (const r of rows) {
        const key = `${r.source}/${chunkYear(r.year)}`;
        if (!need.has(key)) need.set(key, []);
        need.get(key).push(r);
    }
    const results = new Map();   // slug -> card
    await Promise.all(Array.from(need.entries()).map(async ([key, rs]) => {
        const [src, year] = key.split('/');
        try {
            const chunk = await loadChunk(src, year);
            const byId = chunkByIdMap(key, chunk);
            for (const r of rs) {
                const c = byId.get(r.slug);
                if (c) results.set(r.slug, c);
            }
        } catch (e) {
            // missing chunk; skip silently
        }
    }));
    return results;
}
